import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import type { RouterContext } from "./router/routes/__root.js";
import { routeTree } from "./router/routeTree.gen.js";
import { createTrpcClient } from "./trpc/client.js";
import type { WebEnv } from "./config/env.js";
import {
  buildBrowserSessionStart,
  defaultCrashReporter,
  mintBootSessionId,
  readBootUserAgent,
} from "./seams/crashReporter/index.js";

/**
 * Phase 13a — top-level React component.
 *
 * Composes the three providers the Phase 13 client depends on at
 * its outermost layer:
 *
 * 1. `<ClerkProvider>` — owns the auth session. The publishable key
 *    flows in via `env.clerkPublishableKey` (parsed at the boundary
 *    in `config/env.ts`).
 * 2. `<QueryClientProvider>` — TanStack Query cache. 13b's
 *    `useQuery` / `useMutation` calls hang off this; 13a does not
 *    issue any queries yet.
 * 3. `<RouterProvider>` — TanStack Router runtime. The route tree
 *    is generated from `src/router/routes/**` by the Vite plugin.
 *
 * The typed tRPC client is created inside an inner component so it
 * can grab Clerk's `getToken` after `<ClerkProvider>` mounts. The
 * client is held on the router's `context` so loaders / hooks in
 * 13b can read it via `useRouteContext()`.
 */
export type AppProps = {
  readonly env: WebEnv;
};

export function App({ env }: AppProps) {
  return (
    <ClerkProvider publishableKey={env.clerkPublishableKey}>
      {env.clerkPublishableKeySource === "development-placeholder" ? (
        <DevPlaceholderAuthBanner />
      ) : null}
      <AppInner env={env} />
    </ClerkProvider>
  );
}

function DevPlaceholderAuthBanner() {
  return (
    <div
      role="status"
      className="border-b border-warning bg-warning/20 px-4 py-2 text-sm text-fg"
    >
      <strong>Dev mode:</strong> Clerk auth is in placeholder mode (auth
      won&apos;t work). Set <code>VITE_CLERK_PUBLISHABLE_KEY</code> in{" "}
      <code>apps/web/.env.local</code> and restart Vite to enable sign-in.
    </div>
  );
}

function AppInner({ env }: AppProps) {
  // `useAuth().getToken` is a fresh function reference on every
  // Clerk state tick — including session-refresh ticks that fire
  // many times an hour. Holding the latest reference in a ref and
  // keeping it out of the `useMemo` deps means the router /
  // queryClient / crashReporter are built once at mount, not
  // rebuilt (and the query cache + router state wiped) every time
  // Clerk re-emits.
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const { router, queryClient, crashReporter } = useMemo(() => {
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 30_000,
          refetchOnWindowFocus: false,
        },
      },
    });
    const trpc = createTrpcClient({
      apiBaseUrl: env.apiBaseUrl,
      getToken: () => getTokenRef.current(),
    });
    const crashReporter = defaultCrashReporter({
      dsn: env.crashReporterDsn,
      release: env.release,
    });
    const router = createRouter({
      routeTree,
      context: { trpc, queryClient, crashReporter } satisfies RouterContext,
      defaultPreload: "intent",
    });
    return { router, queryClient, crashReporter };
  }, [env.apiBaseUrl, env.crashReporterDsn, env.release]);

  // Start a crash-reporter session once per app boot. The router's
  // root `errorComponent` ships render-time throws under this session
  // id. The platform-globals reaches (`navigator.userAgent`,
  // `crypto.randomUUID`) live behind the `crashReporter` seam —
  // `App.tsx` itself never touches a browser global.
  useEffect(() => {
    const sessionInput = buildBrowserSessionStart({
      sessionId: mintBootSessionId(),
      startedAt: new Date().toISOString(),
      release: env.release,
      userAgent: readBootUserAgent(),
    });
    crashReporter.startSession(sessionInput);
  }, [crashReporter, env.release]);

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createRouterForTypeRegistration>;
  }
}

// Helper used purely to anchor the `Register` declaration above to
// the actual router shape; never invoked at runtime.
function createRouterForTypeRegistration() {
  return createRouter({
    routeTree,
    context: {} as RouterContext,
  });
}
